/**
 * Shared constants for the application.
 * Centralizes property types, price options, districts, and amenities
 * so that FilterBar, AboutPage (form), HousingDetail, and HousingCard
 * all use the same selection values and i18n keys.
 *
 * Amenities are consolidated here as the single source of truth.
 * Each amenity has:
 *   - key: the internal identifier (used in property data + i18n "amenity.*" key)
 *   - icon: emoji for display
 *
 * All components resolve labels via t(`amenity.${key}`) for consistency.
 */

/**
 * Property types displayed in filters and the request form.
 * The `value` matches the `type` field in housingData,
 * and is used directly for filtering and form submission in Vietnamese.
 */
export const PROPERTY_TYPES = [
  { value: 'Ký túc xá', labelKey: 'filter.typeKyTucXa' },
  { value: 'Sleepbox', labelKey: 'filter.typeSleepbox' },
  { value: 'Miniduplex', labelKey: 'filter.typeMiniduplex' },
  { value: 'Phòng (1 - 2 người)', labelKey: 'filter.typePhong' },
];

/**
 * Price options shared across filters and the request form.
 * All values are in VND (numeric string).
 * The first entry is used as the "Any" / "Bất kỳ" placeholder.
 * Each entry has a `labelKey` for i18n translation.
 */
export const PRICE_OPTIONS = [
  { value: '', labelKey: 'filter.anyTypes' },
  { value: '500000', labelKey: 'filter.lessThan500k' },
  { value: '1000000', labelKey: 'filter.price1Mil' },
  { value: '2000000', labelKey: 'filter.price2Mil' },
  { value: '3000000', labelKey: 'filter.price3Mil' },
  { value: '5000000', labelKey: 'filter.price5Mil' },
];

/**
 * Districts as i18n keys used in the request form.
 * These match the district values in housingData (e.g. 'District 1', 'Binh Thanh').
 * The form resolves each key via i18n to display the Vietnamese name.
 */
export const DISTRICT_KEYS = [
  'district1', 'district2', 'district3', 'district4', 'district5',
  'district6', 'district7', 'district8', 'district9', 'district10',
  'district11', 'district12', 'districtBinhThanh', 'districtThuDuc',
  'districtGoVap', 'districtTanBinh', 'districtTanPhu', 'districtPhuNhuan',
  'districtBinhChanh', 'districtCuChi', 'districtHocMon', 'districtNhaBe', 'districtCanGio',
];

/**
 * Consolidated amenities list used across ALL pages:
 * - FilterBar (filters by amenity)
 * - AboutPage / "Find by Request" form
 * - HousingDetail (displays property amenities)
 * - HousingCard (shows amenity count)
 *
 * Labels should be displayed via t(`amenity.${key}`) which provides
 * bilingual support (English + Vietnamese) from a single source.
 *
 * These 14 keys cover all amenities used in housingData and Drive data.
 */
export const AMENITIES_LIST = [
  { key: 'wifi', icon: '📶' },
  { key: 'ac', icon: '❄️' },
  { key: 'waterHeater', icon: '🔥' },
  { key: 'fridge', icon: '🧊' },
  { key: 'cooking', icon: '🍳' },
  { key: 'kitchen', icon: '🍽️' },
  { key: 'washingMachine', icon: '👕' },
  { key: 'laundry', icon: '🧺' },
  { key: 'furniture', icon: '🛋️' },
  { key: 'parking', icon: '🅿️' },
  { key: 'balcony', icon: '🌿' },
  { key: 'cleaning', icon: '🧹' },
  { key: 'elevator', icon: '⬆️' },
  { key: 'security', icon: '📹' },
];

/**
 * Centralized mapping for amenity key normalization.
 * Used by HousingDetail to match raw amenity strings (from property data)
 * to canonical AMENITIES_LIST keys and their i18n translations.
 *
 * Supports multiple spellings in both English and Vietnamese.
 */
export const AMENITY_ALIASES = {
  wifi: ['wifi', 'wify', 'wi-fi'],
  ac: ['ac', 'aircon', 'airconditioner', 'air conditioner', 'air-conditioner', 'máy lạnh', 'dieu hoa', 'điều hòa', 'maylanh'],
  waterHeater: ['waterheater', 'water_heater', 'water heater', 'water-heater', 'máy nước nóng', 'maynuocnong', 'bình nóng lạnh', 'binh nong lanh'],
  parking: ['parking', 'park', 'đỗ xe', 'cho do xe', 'chỗ đỗ xe', 'chỗ để xe', 'bãi xe', 'bai xe'],
  security: ['security', 'camera', 'an ninh', 'camera an ninh', 'baove', 'bảo vệ'],
  kitchen: ['kitchen', 'bep', 'bếp', 'phòng bếp', 'nhà bếp', 'nha bep'],
  laundry: ['laundry', 'wash', 'washing', 'giặt ủi', 'giat ui', 'máy giặt', 'may giat'],
  washingMachine: ['washingmachine', 'washing_machine', 'washing machine', 'máy giặt riêng', 'may giat rieng'],
  cooking: ['cooking', 'cook', 'nấu ăn', 'nau an', 'bếp nấu', 'bep nau'],
  elevator: ['elevator', 'lift', 'thang máy', 'thangmay'],
  furniture: ['furniture', 'furnished', 'nội thất', 'noi that', 'đầy đủ nội thất', 'day du noi that'],
  fridge: ['fridge', 'refrigerator', 'tủ lạnh', 'tulanh'],
  balcony: ['balcony', 'ban công', 'bancong'],
  cleaning: ['cleaning', 'housekeeping', 'dọn phòng', 'don phong', 'dịch vụ dọn phòng', 'dich vu don phong'],
};